import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { galleryAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import {
  Video, Download, ExternalLink,
  Calendar, MapPin, DollarSign, Loader2,
} from 'lucide-react';
import { format, isToday, isThisWeek } from 'date-fns';
import { de as deLocale, enUS as enLocale } from 'date-fns/locale';

/**
 * Video gallery. Every render across all properties.
 *
 * Adds visual rhythm via date-bucketed sections ("Heute" / "Diese
 * Woche" / "Älter") so a Makler who renders daily can scan to today's
 * batch without scrolling past last month's. Bucket headers are
 * suppressed when empty so a sparsely-used account still looks clean.
 *
 * Per-card "Zur Immobilie" affordance is now a visible link so the
 * navigate path is obvious without relying on tap-the-card discovery.
 */

const FILTER_OPTIONS = [
  { value: 'all',        labelKey: 'gallery.filter.all',        styleActive: 'bg-[#1f4d3a]' },
  { value: 'succeeded',  labelKey: 'gallery.filter.ready',      styleActive: 'bg-[#1e7a4f]' },
  { value: 'processing', labelKey: 'gallery.filter.processing', styleActive: 'bg-blue-600' },
];

// Bucket a video into "today / this-week / older" by created_at.
// Anything missing/unparseable falls into "older" so it still surfaces.
function bucketFor(video) {
  const dt = video.created_at ? new Date(video.created_at) : null;
  if (!dt || Number.isNaN(dt.getTime())) return 'older';
  if (isToday(dt)) return 'today';
  if (isThisWeek(dt, { weekStartsOn: 1 })) return 'thisWeek';
  return 'older';
}

export default function VideoGalleryPage() {
  const { t, lang } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const dateLocale = lang === 'de' ? deLocale : enLocale;

  useEffect(() => {
    loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadVideos = async () => {
    try {
      const response = await galleryAPI.getAllVideos();
      setVideos(response.data);
    } catch (error) {
      toast.error(t('gallery.noVideosDesc'));
    } finally {
      setLoading(false);
    }
  };

  // Apply status filter, then bucket. Memoized so filter toggles don't
  // re-bucket the entire list on every render.
  const grouped = useMemo(() => {
    const filtered = videos.filter(v => {
      if (filter === 'all') return true;
      if (filter === 'processing') return v.status === 'processing' || v.status === 'rendering';
      return v.status === filter;
    });
    const buckets = { today: [], thisWeek: [], older: [] };
    for (const v of filtered) buckets[bucketFor(v)].push(v);
    // Within each bucket, newest first.
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    }
    return buckets;
  }, [videos, filter]);

  const totalVisible = grouped.today.length + grouped.thisWeek.length + grouped.older.length;

  const getStatusBadge = (status) => {
    const styles = {
      succeeded: 'bg-[#d6e8dd] text-[#1e7a4f] border-[#1e7a4f]/50',
      processing: 'bg-[#e9efe7] text-[#1f4d3a] border-[#1f4d3a]/50',
      rendering: 'bg-blue-600/20 text-blue-300 border-blue-600/50',
      failed: 'bg-[#a34430]/20 text-[#a34430] border-[#b3543f]/50',
    };
    const labels = {
      succeeded: t('gallery.filter.ready'),
      processing: t('gallery.filter.processing'),
      rendering: t('gallery.filter.processing'),
      failed: t('autoPost.status.failed'),
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${styles[status] || styles.processing}`}>
        {labels[status] || t('gallery.filter.processing')}
      </span>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // Single video card. Pulled out so the bucket sections can map cleanly.
  // For finished renders we show a native <video> element with controls
  // so the user can watch inline + use the browser's built-in fullscreen
  // / scrub / volume. Earlier this was a thumbnail + "Download" button,
  // which forced an extra step for every preview. Reels-style aspect
  // (9:16) so it doesn't look weird stretched into a wide thumbnail.
  const renderCard = (video) => (
    <Card
      key={video.id}
      data-testid={`gallery-video-${video.id}`}
      className="glass border-[#e3dccb] hover:border-[#1f4d3a]/50 transition-all duration-300 overflow-hidden group"
    >
      {/* 9:16 portrait frame. Same approach as the VideoCard component
          on PropertyDetailPage. Earlier I added max-h-[420px] which
          fought the aspect-ratio: on wider grid columns the height
          got capped while width stayed full, leaving a 350×420 box
          with the actual video letterboxed off-center inside.
          Just letting the card take its natural 9:16 height looks
          right at every breakpoint. */}
      <div className="relative aspect-[9/16] bg-black overflow-hidden">
        {video.status === 'succeeded' && video.video_url ? (
          <video
            src={video.video_url}
            poster={video.thumbnail_url || video.property?.images?.[0]}
            className="w-full h-full object-contain bg-black"
            controls
            controlsList="nodownload"
            preload="metadata"
            playsInline
          />
        ) : (
          <>
            <img
              src={video.thumbnail_url || video.property?.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400'}
              alt="Video thumbnail"
              className="w-full h-full object-cover opacity-60"
            />
            {(video.status === 'processing' || video.status === 'rendering' || video.status === 'planned' || video.status === 'waiting') && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
              </div>
            )}
          </>
        )}
        <div className="absolute top-3 right-3 z-10">
          {getStatusBadge(video.status)}
        </div>
      </div>

      <CardContent className="p-4">
        {video.property && (
          <div className="mb-3">
            <h3 className="text-lg font-semibold text-[#23211a] mb-1 line-clamp-1">
              {video.property.title}
            </h3>
            <div className="flex items-center gap-3 text-sm text-[#6f6a5c] mb-2">
              <div className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                <span>{video.property.price}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{video.property.location}</span>
              </div>
            </div>
            {/* Inline "go to property". Visible affordance instead of
                relying on click-the-card discovery. */}
            {video.property_id && (
              <button
                type="button"
                onClick={() => navigate(`/properties/${video.property_id}`)}
                className="inline-flex items-center gap-1 text-xs text-[#1f4d3a] hover:text-[#2e6b50] transition"
              >
                <ExternalLink className="w-3 h-3" />
                {t('gallery.goToProperty')}
              </button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-[#8a8474] mb-3">
          <Calendar className="w-3 h-3" />
          <span>
            {video.created_at && format(new Date(video.created_at), 'PPP', { locale: dateLocale })}
          </span>
        </div>

        {video.status === 'succeeded' && video.video_url ? (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-[#d8d0bd] text-[#4a463b] hover:bg-[#efe9db]"
              onClick={() => {
                // Force a real download (not "open in tab"). Anchor with
                // download attr respects same-origin; for cross-origin we
                // fall back to opening which surfaces the browser's
                // native save dialog.
                const a = document.createElement('a');
                a.href = video.video_url;
                a.download = '';
                a.target = '_blank';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              {t('gallery.downloadVideo')}
            </Button>
          </div>
        ) : (
          <div className="text-center py-2">
            <p className="text-sm text-[#8a8474]">
              {video.status === 'failed' ? t('gallery.generationFailed') : t('gallery.generating')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Bucketed section. Header + grid. Hidden when the bucket is empty
  // so quiet accounts don't show three "Heute (0)" blocks.
  const renderBucket = (key, items) => {
    if (items.length === 0) return null;
    const titleKey =
      key === 'today'    ? 'gallery.bucketToday'    :
      key === 'thisWeek' ? 'gallery.bucketThisWeek' :
                           'gallery.bucketOlder';
    return (
      <section key={key} className="mb-10 last:mb-0">
        <div className="flex items-baseline gap-3 mb-4">
          <h2 className="text-lg font-semibold text-[#23211a]">{t(titleKey)}</h2>
          <span className="text-sm text-[#8a8474]">{items.length}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(renderCard)}
        </div>
      </section>
    );
  };

  return (
    <AppLayout>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-[#23211a] flex items-center gap-3">
              <Video className="w-10 h-10 text-[#8f6f3a]" />
              {t('gallery.title')}
            </h1>
            <p className="text-[#6f6a5c] mt-1">{t('gallery.subtitle')}</p>
          </div>

          {/* Status filter. Kept compact on mobile via wrap. */}
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map(opt => {
              const active = filter === opt.value;
              const count =
                opt.value === 'all'        ? videos.length :
                opt.value === 'processing' ? videos.filter(v => v.status === 'processing' || v.status === 'rendering').length :
                                             videos.filter(v => v.status === opt.value).length;
              return (
                <Button
                  key={opt.value}
                  variant={active ? 'default' : 'outline'}
                  onClick={() => setFilter(opt.value)}
                  className={active ? opt.styleActive : 'border-[#d8d0bd]'}
                >
                  {t(opt.labelKey)} ({count})
                </Button>
              );
            })}
          </div>
        </div>

        {totalVisible === 0 ? (
          <Card className="glass border-[#e3dccb] p-12">
            <div className="text-center">
              <Video className="w-16 h-16 text-[#b5ad99] mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-[#23211a] mb-2">
                {filter === 'all'
                  ? t('gallery.noVideos')
                  : t('gallery.noFilteredVideos', { filter: t(`gallery.filter.${filter === 'succeeded' ? 'ready' : 'processing'}`) })}
              </h2>
              <p className="text-[#6f6a5c] mb-6">
                {filter === 'all'
                  ? t('gallery.createFirst')
                  : t('gallery.noFilteredDesc', { filter: t(`gallery.filter.${filter === 'succeeded' ? 'ready' : 'processing'}`) })
                }
              </p>
              {filter === 'all' && (
                <Button
                  onClick={() => navigate('/properties')}
                  className="bg-[#1f4d3a] hover:bg-[#163a2c]"
                >
                  {t('gallery.createProperty')}
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <>
            {renderBucket('today',    grouped.today)}
            {renderBucket('thisWeek', grouped.thisWeek)}
            {renderBucket('older',    grouped.older)}
          </>
        )}
      </div>
    </AppLayout>
  );
}
